import React from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { companyAPI } from '../../services/api';
import TimelapseAPI from '../../services/timelapseApi';
import { Camera, Image, Play, Plus, ArrowRight } from 'lucide-react';
import LoadingSpinner from '../../components/LoadingSpinner';

const CompanyOverview = () => {
  const timelapseAPI = new TimelapseAPI();
  const { data: cameras, isLoading: camerasLoading } = useQuery('company-cameras', companyAPI.getCameras);
  const { data: timelapseJobs, isLoading: jobsLoading } = useQuery('remote-timelapse-jobs', () => timelapseAPI.getAllJobs());

  const isLoading = camerasLoading || jobsLoading;

  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <LoadingSpinner className="h-64" />
      </div>
    );
  }

  const activeJobs = timelapseJobs?.filter(job => job.status === 'processing' || job.status === 'queued').length || 0;

  const statCards = [
    {
      name: 'Cameras',
      value: cameras?.data?.length || 0,
      icon: Camera,
      color: 'bg-green-500',
      href: '/company/cameras'
    },
    {
      name: 'Active Jobs',
      value: activeJobs,
      icon: Play,
      color: 'bg-orange-500',
      href: '/company/timelapse'
    },
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Overview</h1>
        <p className="mt-2 text-gray-600">
          Welcome to your timelapse management dashboard
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
        {statCards.map((stat) => (
          <div key={stat.name} className="card p-6">
            <div className="flex items-center">
              <div className={`${stat.color} p-3 rounded-lg`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
            </div>
            {stat.href && (
              <div className="mt-4">
                <Link
                  to={stat.href}
                  className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
                >
                  View all <ArrowRight className="h-4 w-4 ml-1" />
                </Link>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="space-y-3">
          <Link
            to="/company/cameras"
            className="block p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Camera className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="font-medium text-gray-900">View Live Cameras</p>
                  <p className="text-sm text-gray-500">Monitor your camera feeds</p>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-gray-400" />
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CompanyOverview;
